/* eslint no-param-reassign: ["error", { "props": false }] -- this controller exists to mutate DOM element state */
import { Controller } from "@hotwired/stimulus";

// Drives the read/write grant matrix. Every row owns hidden column panels
// (all-columns checkbox plus one checkbox per column): one for read, or one
// each for the insert and update columns of a write row. Each panel belongs to
// a "kind" and is enabled by the row's column gate of the same kind
// (data-column-gate). Editing columns moves that panel into the shared dialog
// and back afterwards, so its inputs never leave the surrounding form.
// Required columns (data-required) are always checked and locked. While the dialog is open the rest of the page
// is inert, Tab is trapped inside and focus returns to the opening button.
export default class extends Controller {
  static targets = [
    "row",
    "search",
    "filter",
    "dialog",
    "window",
    "title",
    "body",
    "deselect",
  ];

  static values = { all: String, partial: String, none: String };

  connect() {
    this.filter = "all";
    this.current = null;
    this.rowTargets.forEach((row) => this.refreshRow(row));
    this.applyFilter();
  }

  rowChanged(event) {
    this.refreshRow(event.target.closest("tr"));
    this.applyFilter();
  }

  setFilter(event) {
    this.filter = event.currentTarget.dataset.filter;
    this.applyFilter();
  }

  applyFilter() {
    const query = this.searchTarget.value.trim().toLowerCase();

    this.rowTargets.forEach((row) => {
      const matchesQuery = row.dataset.table.includes(query);
      const matchesFilter =
        this.filter === "all" ||
        (this.filter === "active") === (row.dataset.active === "true");
      row.classList.toggle("hidden", !(matchesQuery && matchesFilter));
    });

    this.filterTargets.forEach((button) => {
      const selected = button.dataset.filter === this.filter;
      button.setAttribute("aria-pressed", selected);
      button.classList.toggle("bg-gray-100", selected);
      button.classList.toggle("text-gray-900", selected);
      button.classList.toggle("text-gray-500", !selected);
    });
  }

  open(event) {
    const row = event.currentTarget.closest("tr");
    const { kind } = event.currentTarget.dataset;
    const panel = this.panelFor(row, kind);

    this.current = {
      row,
      kind,
      panel,
      home: panel.parentElement,
      opener: event.currentTarget,
    };
    this.titleTarget.textContent =
      event.currentTarget.dataset.title || row.dataset.table;
    this.bodyTarget.appendChild(panel);
    panel.classList.remove("hidden");
    this.dialogTarget.classList.remove("hidden");
    this.refreshDeselect();
    this.makeBackgroundInert();
    (panel.querySelector("input:not(:disabled)") || this.windowTarget).focus();
  }

  close() {
    if (!this.current) return;

    const { row, panel, home, opener } = this.current;
    panel.classList.add("hidden");
    home.appendChild(panel);
    this.dialogTarget.classList.add("hidden");
    this.current = null;
    this.restoreBackground();
    this.refreshRow(row);
    opener.focus();
  }

  keydown(event) {
    if (!this.current) return;
    if (event.key === "Escape") {
      this.close();
      return;
    }
    if (event.key === "Tab") this.trapFocus(event);
  }

  trapFocus(event) {
    const focusable = Array.from(
      this.windowTarget.querySelectorAll(
        "input:not(:disabled), button:not(.hidden)"
      )
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (!this.windowTarget.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // Marks every element outside the dialog's ancestor chain inert, so the
  // background is neither focusable nor exposed to assistive technology.
  makeBackgroundInert() {
    this.inerted = [];
    for (
      let node = this.dialogTarget;
      node !== document.body;
      node = node.parentElement
    ) {
      Array.from(node.parentElement.children).forEach((sibling) => {
        if (sibling === node || sibling.inert || sibling.tagName === "SCRIPT")
          return;
        sibling.inert = true;
        this.inerted.push(sibling);
      });
    }
  }

  restoreBackground() {
    this.inerted.forEach((element) => {
      element.inert = false;
    });
    this.inerted = [];
  }

  toggleAll(event) {
    const all = event.target;
    all
      .closest("[data-panel]")
      .querySelectorAll("[data-column]")
      .forEach((column) => {
        if (column.dataset.required) return;
        if (all.checked) column.checked = true;
        column.disabled = all.checked;
      });
    this.refreshDeselect();
  }

  // Clears every individual column of the open panel; only offered while
  // "all columns" is off, since the columns are locked otherwise.
  deselectColumns() {
    this.current.panel.querySelectorAll("[data-column]").forEach((column) => {
      if (!column.dataset.required) column.checked = false;
    });
  }

  refreshDeselect() {
    const allColumns = this.current.panel.querySelector("[data-all]").checked;
    this.deselectTarget.classList.toggle("hidden", allColumns);
  }

  refreshRow(row) {
    const gates = Array.from(row.querySelectorAll("[data-gate]"));
    const active = gates.some((gate) => gate.checked);
    row.dataset.active = String(active);
    row.classList.toggle("bg-blue-50", active);

    // An editor (summary and pencil) shows while the gate of its kind is on.
    let editable = false;
    row.querySelectorAll("[data-editor]").forEach((editor) => {
      const { kind } = editor.dataset;
      const gate = row.querySelector(`[data-column-gate="${kind}"]`);
      editable = editable || gate.checked;
      editor.classList.toggle("hidden", !gate.checked);
      editor.classList.toggle("flex", gate.checked);

      const text = this.summaryFor(this.panelFor(row, kind));
      const summary = editor.querySelector("[data-summary]");
      summary.textContent = text.label;
      summary.classList.toggle("text-red-600", text.empty);
      summary.classList.toggle("text-gray-600", !text.empty);
    });
    row.querySelector("[data-dash]").classList.toggle("hidden", editable);
  }

  summaryFor(panel) {
    if (panel.querySelector("[data-all]").checked)
      return { label: this.allValue, empty: false };

    const columns = Array.from(panel.querySelectorAll("[data-column]"));
    const count = columns.filter((column) => column.checked).length;
    if (count === 0) return { label: this.noneValue, empty: true };

    const label = this.partialValue
      .replace("__count__", count)
      .replace("__total__", columns.length);
    return { label, empty: false };
  }

  panelFor(row, kind) {
    return this.current &&
      this.current.row === row &&
      this.current.kind === kind
      ? this.current.panel
      : row.querySelector(`[data-panel][data-kind="${kind}"]`);
  }
}
